// React provider + hooks for the HubSpot iframe session.
//
// Thin React wrapper over ./hubspot-handshake.ts. The handshake module owns
// the postMessage protocol and token lifecycle; this file converts its
// observable state into the <SessionProvider> context the UI consumes via
// useSession().
//
// Why a separate module from the handshake:
//   - The handshake logic has to be driven by raw timers and events; keeping
//     React out of it means it is cheap to unit-test without jsdom.
//   - React can mount/remount around the handshake, but the handshake lives
//     for the lifetime of the iframe. Keeping it outside the React tree
//     avoids losing the already-received session on StrictMode double-mount.
//
// Public surface:
//   - SessionProvider: wraps the app.
//   - useSession(): returns a stable object the UI consumes. Throws if the
//     provider is missing (programming error, not a runtime refusal).
//   - useOptionalSession(): same but returns null when not yet ready.
//   - useRequireSession(): asserts status === 'ready' and returns the
//     session + helpers directly, for screens that render inside a boundary.

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

import {
  createHandshake,
  type Handshake,
  type HandshakeState,
  type HubSpotSession,
  type HubSpotWorkContext,
} from './hubspot-handshake.js';

export type SessionStatus = HandshakeState['status'];

export interface SessionContextValue {
  status: SessionStatus;
  session: HubSpotSession | null;
  error: HandshakeState['error'];
  // Imperatively request a fresh session. The handshake also auto-refreshes
  // at 80% of the TTL; callers rarely need this.
  refresh(): void;
  // W27 (2026-10-20): move the live session to another contact record so
  // every subsequent request carries the new X-Customer-Id (and X-Work-Id
  // when a queue work item is supplied). Returns false when no session is
  // live or the handshake implementation does not support it.
  setCustomerId(objectId: string, work?: HubSpotWorkContext | null): boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export interface SessionProviderProps {
  children: ReactNode;
  // Test-only: skip the real handshake and use the dev mock.
  forceMock?: boolean;
  // Test-only injectable Window. Defaults to the global window.
  windowRef?: Window;
  // Test-only pre-built handshake. When provided, SessionProvider does not
  // create its own and will not tear it down on unmount. Lets tests drive
  // the handshake directly.
  handshake?: Handshake;
  // Test-only clock seam, forwarded to createHandshake.
  now?: () => number;
}

export function SessionProvider(props: SessionProviderProps): ReactNode {
  const { children, forceMock, windowRef, handshake, now } = props;
  // Keep the handshake in a ref so it survives StrictMode's double-mount
  // (React 18 intentionally mounts + unmounts + remounts a component once in
  // development; without this, the handshake would be torn down and the
  // first 'hubspot.session' message would be dropped).
  const handshakeRef = useRef<Handshake | null>(handshake ?? null);
  const ownsHandshake = useRef<boolean>(!handshake);

  // Create the handshake lazily so we can respect windowRef / forceMock /
  // now at mount time and not fire during SSR.
  if (!handshakeRef.current) {
    handshakeRef.current = createHandshake({
      ...(windowRef !== undefined ? { windowRef } : {}),
      ...(forceMock !== undefined ? { forceMock } : {}),
      ...(now !== undefined ? { now } : {}),
    });
  }

  // Subscribe to the handshake's state via useSyncExternalStore so React
  // schedules updates on the proper phase.
  const subscribe = useCallback((cb: () => void) => {
    const unsub = handshakeRef.current!.subscribe(() => cb());
    return () => unsub();
  }, []);
  const getSnapshot = useCallback(
    () => handshakeRef.current!.getState(),
    [],
  );
  const getServerSnapshot = useCallback(
    () => handshakeRef.current!.getState(),
    [],
  );
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Tear down the handshake when the provider unmounts and it owns the
  // handshake. In production this effectively never fires because the
  // provider sits at the app root, but it keeps tests clean.
  useEffect(() => {
    return () => {
      if (ownsHandshake.current && handshakeRef.current) {
        handshakeRef.current.teardown();
      }
    };
  }, []);

  const refresh = useCallback(() => {
    // Re-posting hello is sufficient: HubSpot responds with a fresh
    // 'hubspot.session'. The handshake listener handles the rest.
    const win = windowRef ?? (typeof window !== 'undefined' ? window : null);
    if (!win) return;
    const parent = win.parent;
    if (!parent || parent === win) return;
    try {
      parent.postMessage(
        { type: 'saga.session.refresh', version: 1 },
        '*',
      );
    } catch {
      /* non-fatal */
    }
  }, [windowRef]);

  const setCustomerId = useCallback(
    (objectId: string, work?: HubSpotWorkContext | null): boolean => {
      const h = handshakeRef.current;
      if (!h || typeof h.setContext !== 'function') return false;
      return h.setContext({ objectType: 'contact', objectId }, work);
    },
    [],
  );

  const value = useMemo<SessionContextValue>(
    () => ({
      status: state.status,
      session: state.session,
      error: state.error,
      refresh,
      setCustomerId,
    }),
    [state, refresh, setCustomerId],
  );

  return createElement(SessionContext.Provider, { value }, children);
}

// Return the full context value. Throws if used outside a <SessionProvider>.
export function useSession(): SessionContextValue {
  const v = useContext(SessionContext);
  if (!v) {
    throw new Error(
      'useSession() must be called inside <SessionProvider>. Wrap the app ' +
        'root with <SessionProvider /> before mounting routes.',
    );
  }
  return v;
}

// Same but non-throwing. Returns null when no provider is present. Rarely
// what you want; use <SessionProvider> higher up instead.
export function useOptionalSession(): SessionContextValue | null {
  return useContext(SessionContext);
}

// Narrows the status to 'ready' and returns the session object directly.
// Throws if status is not 'ready'. Screens render inside a boundary that
// renders a fallback for pending / error / expired, so this is safe inside
// those screens and reads cleanly.
export function useRequireSession(): HubSpotSession & {
  // Mirror of session.context.objectId for ergonomics. The brief calls this
  // customerId in the headers, so expose both spellings to callers.
  customerId: string;
  workId: string | null;
} {
  const v = useSession();
  if (v.status !== 'ready' || !v.session) {
    throw new Error(
      `useRequireSession() called with status=${v.status}. Wrap the caller ` +
        'in a boundary that renders a fallback until status === "ready".',
    );
  }
  const s = v.session;
  return {
    ...s,
    customerId: s.context.objectId,
    workId: s.work ? s.work.workId : null,
  };
}
